import React, { useState } from "react";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { OverlayPanel } from "primereact/overlaypanel";
import { useNewIssuesNotification } from "../../hooks/useNewIssuesNotification";
import { Card } from "primereact/card";

interface NewIssuesBannerProps {
  collections: string[];
  position?: "top" | "bottom";
  showCount?: boolean;
}

const NewIssuesBanner: React.FC<NewIssuesBannerProps> = ({
  collections,
  position = "top",
  showCount = true,
}) => {
  const [overlayVisible, setOverlayVisible] = useState(false);
  const overlayRef = React.useRef<OverlayPanel>(null);

  // Create individual notification hooks for each collection
  const syncOwnershipNotifications = useNewIssuesNotification({
    collectionName: "SoundRecordingHealthMetrics",
    thresholdMinutes: 5,
    enableNotifications: true,
  });

  const musicVideoNotifications = useNewIssuesNotification({
    collectionName: "MusicVideoHealthMetrics",
    thresholdMinutes: 5,
    enableNotifications: true,
  });

  // Combine all notifications
  const allNotifications = [
    syncOwnershipNotifications,
    musicVideoNotifications,
  ];

  const totalNewIssues = allNotifications.reduce(
    (sum, notification) => sum + notification.totalNewIssues,
    0
  );

  const hasAnyNewIssues = allNotifications.some(
    (notification) => notification.hasNewIssues
  );

  const allNewIssues = allNotifications
    .flatMap((notification) => notification.newIssues)
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  const handleBannerClick = (event: React.MouseEvent) => {
    if (overlayRef.current) {
      overlayRef.current.toggle(event);
      setOverlayVisible(!overlayVisible);
    }
  };

  const handleClearAll = () => {
    allNotifications.forEach((notification) => notification.clearNewIssues());
  };

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  if (!hasAnyNewIssues) {
    return null;
  }

  return (
    <>
      {/* Banner */}
      <div
        className={`fixed ${
          position === "top" ? "top-0" : "bottom-0"
        } left-0 right-0 z-50 bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg cursor-pointer transition-all duration-300 hover:shadow-xl`}
        onClick={handleBannerClick}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 app-menubar-title rounded-full animate-pulse"></div>
            <span className="font-medium">
              New issues detected in your music asset audits!
            </span>
            {showCount && totalNewIssues > 0 && (
              <Badge value={totalNewIssues} severity="danger" />
            )}
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm opacity-90">Click to view details</span>
            <i className="pi pi-chevron-down text-sm"></i>
          </div>
        </div>
      </div>

      {/* Overlay Panel */}
      <OverlayPanel ref={overlayRef} className="w-96 max-h-96 overflow-y-auto">
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">New Issues</h3>
            <Button
              label="Clear All"
              icon="pi pi-trash"
              size="small"
              severity="danger"
              outlined
              onClick={handleClearAll}
            />
          </div>

          {allNewIssues.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <i className="pi pi-check-circle text-3xl mb-2"></i>
              <p>No new issues to display</p>
            </div>
          ) : (
            <div className="space-y-3">
              {allNewIssues.map((notification) => (
                <Card key={notification.id} className="p-3 shadow-sm">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-sm mb-1">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatTimestamp(notification.timestamp)}
                      </p>
                      {notification.data.Song_Name && (
                        <p className="text-xs text-gray-600 mt-1">
                          Song: {notification.data.Song_Name}
                        </p>
                      )}
                    </div>

                    <Button
                      icon="pi pi-times"
                      size="small"
                      text
                      severity="secondary"
                      onClick={() => {
                        const collectionNotification = allNotifications.find(
                          (n) =>
                            n.newIssues.some(
                              (issue) => issue.id === notification.id
                            )
                        );
                        if (collectionNotification) {
                          collectionNotification.markAsRead(notification.id);
                        }
                      }}
                    />
                  </div>
                </Card>
              ))}
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="text-xs text-gray-500 text-center">
              Real-time monitoring active for {collections.length} collection
              {collections.length !== 1 ? "s" : ""}
            </div>
          </div>
        </div>
      </OverlayPanel>
    </>
  );
};

export default NewIssuesBanner;
